/* eslint-disable */
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  sidebarRoutes,
  SidebarRoute as SidebarRouteType,
} from "ui/pages/layouts/components/SidebarRoutes";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { selectUserName } from "domain/auth/auth.selectors";
import { translate } from "ui/i18n";
import { LogoInnizio } from "ui/assets/svg/LogoInnizio";
import { HiChat, HiChevronDown, HiLogout } from "react-icons/hi";
import { useWindowWidth } from "ui/utils/hooks";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { SidebarContext } from "ui/pages/layouts/components/SidebarContext";
import { logoutUser } from "domain/auth/auth.actions";

export function Sidebar() {
  const { isSidebarOpen, setIsSidebarOpen } = useContext(SidebarContext);
  const isDesktop = useWindowWidth();
  const navigate = useNavigate();
  const userName = useAppSelector(selectUserName);
  const dispatch = useAppDispatch();

  const handleLogout = async () => {
    await dispatch(logoutUser());
  };

  const openContactInnizio = () => {
    navigate("/contact-innizio");
  };

  return (
    <div className="!z-50 flex h-dvh min-h-full w-[80vw] flex-col justify-between bg-white shadow-2xl shadow-white/5 md:fixed md:h-full md:w-64">
      <div>
        <Link to="/account" className="flex flex-row items-center px-6 py-7">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondaryGrey-800 text-2xl font-medium text-white ">
            {userName?.charAt(0).toUpperCase()}
          </div>

          <div className="ms-3 flex flex-col">
            <span className="text-sm font-medium text-darkGrey-800">
              {translate("ns1:Sidebar.WelcomeBack")}
            </span>
            <span className="mt-0.5 text-base font-bold text-darkGrey-800">
              {userName}
            </span>
          </div>
        </Link>
        <div className="mb-6 h-px bg-darkGrey-200 md:mb-8" />

        <ul className="mb-auto">
          {sidebarRoutes.map((route, index) => (
            <div key={index}>
              {route.children ? (
                <SidebarRouteWithChildren
                  route={route}
                  setIsSidebarOpen={setIsSidebarOpen}
                />
              ) : (
                <SidebarRouteWithoutChildren
                  route={route}
                  setIsSidebarOpen={setIsSidebarOpen}
                />
              )}
            </div>
          ))}
        </ul>
      </div>

      <div>
        <div className="mb-6 h-px bg-darkGrey-200 md:mb-8" />
        <ul>
          <div className="relative flex hover:cursor-pointer">
            <li
              className={`flex w-full cursor-pointer items-center ${
                isDesktop ? "h-[48px]" : "h-[44px]"
              }`}
            >
              <button
                className="flex h-full w-full items-center px-6 py-[10px] font-medium text-darkGrey-800"
                onClick={openContactInnizio}
              >
                <div className="w-[20px]">
                  <HiChat className="text-secondaryGrey-800" size={20} />
                </div>
                <p className="ml-4 text-sm font-medium text-darkGrey-800 md:text-base">
                  {translate("ns1:Sidebar.ContactInnizio")}
                </p>
              </button>
            </li>
          </div>
          <div className="relative flex hover:cursor-pointer">
            <li
              className={`flex w-full cursor-pointer items-center ${
                isDesktop ? "h-[48px]" : "h-[40px]"
              }`}
            >
              <button
                className="flex h-full w-full items-center px-6 py-[10px] font-medium text-darkGrey-800"
                onClick={handleLogout}
              >
                <HiLogout className="text-secondaryGrey-800" size={20} />{" "}
                <p className="ml-4 text-sm font-medium text-darkGrey-800 md:text-base">
                  {translate("ns1:Sidebar.Logout")}
                </p>
              </button>
            </li>
          </div>
        </ul>
        <div className="flex justify-center px-6 pb-5 pt-7 md:pt-12">
          <LogoInnizio width={80} />
        </div>
        {!isDesktop && isSidebarOpen && (
          <div
            className="absolute left-[80vw] top-0 z-20 min-h-dvh w-[20vw] cursor-pointer"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

const SidebarRouteWithoutChildren = ({
  route,
  setIsSidebarOpen,
}: {
  route: SidebarRouteType;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const isDesktop = useWindowWidth();
  const [searchParams] = useSearchParams();
  const isRouteActive = (routeName: string) => {
    //hightlight my-creations instead of seances when on the seance page with isPersonal=true
    if (
      routeName === "/my-creations" &&
      searchParams.get("isPersonal") === "true"
    ) {
      return true;
    }
    if (routeName === "/seances" && searchParams.get("isPersonal") === "true") {
      return false;
    }
    return location.pathname === routeName;
  };

  return (
    <div className="relative flex hover:cursor-pointer">
      <li
        className={`mx-3 flex w-full cursor-pointer items-center rounded-lg ${
          isRouteActive(route.path) ? "bg-darkGrey-100" : ""
        } ${isDesktop ? "h-[48px]" : "h-[44px]"}`}
      >
        <Link
          to={route.path}
          className={`flex h-full w-full items-center px-3 text-secondaryGrey-800 ${
            isRouteActive(route.path) ? "font-bold" : "font-medium"
          }`}
          onClick={() => setIsSidebarOpen(false)}
        >
          {route.icon ? (
            <route.icon className="text-secondaryGrey-800" size={20} />
          ) : (
            <span style={{ width: 20 }} />
          )}{" "}
          <p
            className={`ml-4 flex text-sm md:text-base ${
              isRouteActive(route.path)
                ? "font-bold text-darkGrey-900"
                : "font-medium text-darkGrey-800"
            }`}
          >
            {route.name}
          </p>
        </Link>
      </li>
    </div>
  );
};

const SidebarRouteWithChildren = ({
  route,
  setIsSidebarOpen,
}: {
  route: SidebarRouteType;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const isDesktop = useWindowWidth();
  const isRouteActive = (routeName: string) => {
    return location.pathname === routeName;
  };
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <div className="relative flex hover:cursor-pointer">
        <li
          className={`mx-3 flex w-full cursor-pointer items-center rounded-lg px-3 ${
            isDesktop ? "h-[48px]" : "h-[40px]"
          }`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex w-full items-center justify-between">
            <span className="flex w-full items-center">
              {route.icon ? (
                <route.icon className="text-secondaryGrey-800" size={20} />
              ) : (
                <span style={{ width: 20 }} />
              )}{" "}
              <p
                className={`ml-4 flex text-sm md:text-base ${
                  isRouteActive(route.path)
                    ? "font-bold text-darkGrey-900"
                    : "font-medium text-darkGrey-800"
                }`}
              >
                {route.name}
              </p>
            </span>
            <HiChevronDown
              size={24}
              className={`ms-1 ${isOpen ? "popover-open" : "popover-close"}`}
            />
          </div>
        </li>
      </div>
      {isOpen &&
        route.children?.map((child, index) => (
          <SidebarRouteWithoutChildren
            key={index}
            route={child}
            setIsSidebarOpen={setIsSidebarOpen}
          />
        ))}
    </>
  );
};
